import { DotOutline } from "@phosphor-icons/react";

import { SearchInputFilterItems } from "features/filters/components";

import RenderIf from "common/components/RenderIf";
import ProfileImage from "common/components/ui/ProfileImage";
import { CAMPAIGN_STATES } from "common/constants";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useWorkspaceAccounts from "common/datahooks/useWorkspaceAccounts";
import { typedObjectEntries } from "common/helpers/utils";
import type { CampaignState } from "common/types";

import { CAMPAIGNS_FILTERS } from "../../constants";

import type { ComponentProps } from "react";
import type { CampaignsSearchParams } from "../../types";

import LicenseIcon from "assets/icons/license.svg?react";
import RentedIcon from "assets/icons/plans/avatar-outline-small.svg?react";

export default function CampaignFilterOptions<
  SearchParamsKey extends keyof CampaignsSearchParams,
>({
  searchParamsKey,
  searchParams,
  setSearchParams,
}: {
  searchParamsKey: SearchParamsKey;
  searchParams: CampaignsSearchParams;
  setSearchParams: (searchParams: CampaignsSearchParams) => void;
}) {
  const { id } = useSelectedWorkspaceContext();
  const { workspaceAccounts, isLoadingWorkspaceAccounts } =
    useWorkspaceAccounts(id);

  function toggleParam(
    optionKey: CampaignsSearchParams[SearchParamsKey][number],
  ) {
    const isSelected = searchParams[searchParamsKey].find(
      (value) => value === optionKey,
    );
    const updatedParams = isSelected
      ? searchParams[searchParamsKey].filter(
          (selectedParam) => selectedParam !== optionKey,
        )
      : [...searchParams[searchParamsKey], optionKey];

    setSearchParams({
      ...searchParams,
      [searchParamsKey]: updatedParams,
    });
  }

  const commonProps: Pick<
    ComponentProps<typeof SearchInputFilterItems>,
    "getIsChecked" | "onSelectionChange"
  > = {
    getIsChecked: ({ id }) =>
      !!searchParams[searchParamsKey].find((value) => value === id),
    onSelectionChange: (type, { id }) => {
      if (type === "toggle") {
        toggleParam(id);
      } else if (type === "select") {
        setSearchParams({ ...searchParams, [searchParamsKey]: [id] });
      }
    },
  };

  if (searchParamsKey === "accounts") {
    return (
      <SearchInputFilterItems
        {...commonProps}
        getFilteredItems={(lowercaseSearchKeyword) =>
          workspaceAccounts.filter(({ full_name }) =>
            full_name.toLowerCase().includes(lowercaseSearchKeyword),
          )
        }
        renderLabel={({ full_name }) => full_name}
        renderLabelPrefixComponent={({ picture_url, type, license }) => (
          <div className="relative shrink-0">
            <ProfileImage size="xs" src={picture_url} />
            <RenderIf condition={type === "rental"}>
              <RentedIcon className="absolute -bottom-1 -right-[5px]" />
            </RenderIf>
            <RenderIf condition={type === "user" && !!license}>
              <LicenseIcon className="absolute -bottom-1 -right-[5px]" />
            </RenderIf>
          </div>
        )}
        isLoading={isLoadingWorkspaceAccounts}
      />
    );
  }
  return (
    <SearchInputFilterItems
      {...commonProps}
      getFilteredItems={(lowercaseSearchKeyword) =>
        typedObjectEntries(CAMPAIGNS_FILTERS[searchParamsKey].options)
          .filter(([, label]) =>
            label.toLowerCase().includes(lowercaseSearchKeyword),
          )
          .map(([key]) => ({ id: key }))
      }
      renderLabel={({ id }) => CAMPAIGNS_FILTERS[searchParamsKey].options[id]}
      renderLabelPrefixComponent={
        searchParamsKey === "status"
          ? ({ id }) => (
              <DotOutline
                size={20}
                weight="fill"
                className={CAMPAIGN_STATES[id as CampaignState].iconColor}
              />
            )
          : undefined
      }
      isLoading={false}
    />
  );
}
